import React, { useEffect, useState } from 'react';
import styles from './DirectLoadAnimation.module.scss';

/**
 * DirectLoadAnimation
 * Premium full-screen loading overlay for direct video loading in Playground.
 * Shows when a coach navigates from CreateFeedback to load a specific video.
 */
export default function DirectLoadAnimation({ videoTitle, isVisible, onComplete }) {
    const [progress, setProgress] = useState(0);

    useEffect(() => {
        if (!isVisible) {
            setProgress(0);
            return;
        }

        // Animate progress from 0 to 100 over 1.8 seconds
        const duration = 1800;
        const startTime = Date.now();

        const interval = setInterval(() => {
            const elapsed = Date.now() - startTime;
            const newProgress = Math.min((elapsed / duration) * 100, 100);
            setProgress(newProgress);

            if (newProgress >= 100) {
                clearInterval(interval);
                // Small delay before calling onComplete for smooth transition
                setTimeout(() => {
                    if (onComplete) onComplete();
                }, 200);
            }
        }, 16); // ~60fps

        return () => clearInterval(interval);
    }, [isVisible, onComplete]);

    if (!isVisible) return null;

    return (
        <div className={styles.overlay}>
            <div className={styles.content} role="status" aria-live="polite" aria-atomic="true">
                {/* Animated shuttlecock icon */}
                <div className={styles.iconWrapper}>
                    <img
                        src="/playground/shuttle.png"
                        alt="Shuttlecock"
                        className={styles.shuttleIcon}
                    />
                    <div className={styles.glowRing} />
                </div>

                {/* Video title */}
                <h2 className={styles.title}>
                    Loading <span className={styles.highlight} title={videoTitle || 'Match Video'}>{videoTitle || 'Match Video'}</span>
                </h2>

                {/* Subtitle message */}
                <p className={styles.subtitle}>
                    Please hold tight while we set up the SmartInsights environment
                </p>

                {/* Progress bar */}
                <div className={styles.progressBar}>
                    <div
                        className={styles.progressFill}
                        style={{ width: `${progress}%` }}
                    />
                </div>

                {/* Progress percentage */}
                <div className={styles.progressText}>
                    {Math.round(progress)}%
                </div>

                {/* Animated particles */}
                <div className={styles.particles}>
                    {Array.from({ length: 12 }).map((_, i) => (
                        <div
                            key={i}
                            className={styles.particle}
                            style={{
                                '--delay': `${i * 0.15}s`,
                                '--angle': `${i * 30}deg`
                            }}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
}
